import { useMemo } from 'react'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import {
  PiHandshake,
  PiChat,
  PiPaperPlaneTilt,
  PiTrendUp,
  PiUsers,
  PiWarningCircle,
} from 'react-icons/pi'

import { Alert } from 'components/FbUI/Alert'
import { TargetArrowIcon } from 'components/Layout/LeftNav/nav-icons'
import { cn } from 'components/UI/cn'
import type { Campaign } from 'models/campaign'
import { WORKFLOW_STATUS } from 'models/campaign'
import { useGetReplies, type Reply } from 'features/campaigns/replies_api'
import {
  useGetCampaignSequence,
  type Sequence,
} from 'features/campaigns/sequences_api'
import { groupStepsByFamily } from 'features/campaigns/sequenceVariants'
import {
  useGetCampaignInsights,
  useGetCampaignLaunchReadiness,
} from 'services/campaigns'
import { formatInteger } from 'utils/formatting'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import type { CampaignInsightsSequenceStepStat } from 'services/generated'

interface CampaignDashboardOverviewTabProps {
  campaign: Campaign
  campaignId: number
  campaignStatus: string
  onTabChange?: (tab: string) => void
  onViewSequence?: () => void
}

const SENTIMENT_CONFIG = {
  positive: {
    label: 'Interested',
    color: 'bg-green-50 text-green-700',
  },
  negative: {
    label: 'Not Interested',
    color: 'bg-gray-100 text-gray-700',
  },
  neutral: {
    label: 'Neutral',
    color: 'bg-white border border-gray-300 text-gray-500',
  },
} as const

function getSentiment(reply: Reply): keyof typeof SENTIMENT_CONFIG {
  if (reply.is_interested === true) return 'positive'
  if (reply.is_interested === false) return 'negative'
  return 'neutral'
}

import { formatRelativeTime } from 'utils/formatting'

function getReplyPreview(reply: Reply): string {
  if (reply.text_body) {
    return (
      reply.text_body.slice(0, 100) +
      (reply.text_body.length > 100 ? '...' : '')
    )
  }
  if (reply.html_body) {
    const stripped = reply.html_body.replace(/<[^>]*>/g, '')
    return stripped.slice(0, 100) + (stripped.length > 100 ? '...' : '')
  }
  return reply.subject || 'No preview available'
}

function toNumber(value: string | number | null | undefined): number {
  if (typeof value === 'number') return value
  if (typeof value === 'string') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : 0
  }
  return 0
}

export function CampaignDashboardOverviewTab({
  campaign,
  campaignId,
  campaignStatus,
  onTabChange,
  onViewSequence,
}: CampaignDashboardOverviewTabProps) {
  const isPaused =
    campaignStatus === 'paused' ||
    campaignStatus === 'stopped' ||
    campaign.workflow_status === WORKFLOW_STATUS.PAUSED
  const isComplete =
    campaignStatus === 'completed' ||
    campaignStatus === 'archived' ||
    campaignStatus === 'deleted' ||
    campaign.workflow_status === WORKFLOW_STATUS.COMPLETE

  const { data: sequence } = useGetCampaignSequence(campaignId, !!campaign)
  const { data: insights } = useGetCampaignInsights(campaignId, !!campaign)
  const { data: readiness } = useGetCampaignLaunchReadiness(
    campaignId,
    !!campaign
  )
  const bisonCampaignId = sequence?.bison_campaign_id ?? 0
  const { data: repliesData } = useGetReplies({ campaign_id: bisonCampaignId })
  const recentReplies = useMemo(
    () => repliesData?.results.slice(0, 3) ?? [],
    [repliesData]
  )

  const stats = useMemo(
    () => ({
      targets: campaign.contact_company_count ?? 0,
      contacts: campaign.contact_count ?? 0,
      contactsInSequence: readiness?.leads.count,
      deals: campaign.deal_count ?? 0,
      emailsSent: toNumber(insights?.summary.emails_sent),
      replyRate: toNumber(
        insights?.summary.unique_replies_per_contact_percentage
      ),
      // Share of enrolled contacts with an interested reply (FIR-7630):
      // distinct interested leads (server-computed) over total enrolled
      // contacts, not Bison's reply-based interested_percentage. A zero
      // denominator yields 0, which the card renders as '—'. Clamped to 100:
      // the numerator counts distinct leads while the denominator is the
      // denormalized contact_count, which can lag (FIR-7563), so the ratio
      // can briefly exceed 1 — never show a >100% rate.
      interestedRate:
        (campaign.contact_count ?? 0) > 0
          ? Math.min(
              100,
              ((insights?.interested_count ?? 0) /
                (campaign.contact_count ?? 0)) *
                100
            )
          : 0,
      // Computed server-side from the same predicate as the contact
      // "Bounced" chip, so this number always matches the contacts table.
      bounced: insights?.bounced_count ?? 0,
    }),
    [campaign, insights, readiness]
  )

  const hasInSequenceData =
    stats.contacts > 0 && stats.contactsInSequence !== undefined
  const contactsValue = hasInSequenceData
    ? `${formatInteger(stats.contactsInSequence)} of ${formatInteger(stats.contacts)}`
    : formatInteger(stats.contacts) || '—'
  const contactsSublabel = hasInSequenceData ? 'in sequence' : undefined

  const statCards: Array<{
    label: string
    value: string
    sublabel?: string
    icon: React.ComponentType<{ size?: number; className?: string }>
    /** Marks deliverability-health tiles: red icon chip + red value. */
    isDanger?: boolean
    tooltip?: string
  }> = [
    {
      label: 'Targets',
      value: formatInteger(stats.targets) || '—',
      icon: TargetArrowIcon,
    },
    {
      label: 'Contacts',
      value: contactsValue,
      sublabel: contactsSublabel,
      icon: PiUsers,
    },
    {
      label: 'Emails Sent',
      value: stats.emailsSent > 0 ? formatInteger(stats.emailsSent) : '—',
      icon: PiPaperPlaneTilt,
    },
    {
      label: 'Reply Rate',
      value: stats.replyRate > 0 ? `${stats.replyRate.toFixed(1)}%` : '—',
      icon: PiChat,
    },
    {
      label: 'Interested Rate',
      value:
        stats.interestedRate > 0 ? `${stats.interestedRate.toFixed(1)}%` : '—',
      icon: PiTrendUp,
    },
    {
      // Always rendered, including 0: deliverability health is worth
      // seeing when it is good. Red only when there is something to act
      // on; a zero renders neutral so healthy campaigns show no warning.
      label: 'Bounced',
      value: formatInteger(stats.bounced),
      icon: PiWarningCircle,
      isDanger: stats.bounced > 0,
      tooltip: 'Contacts whose email bounced. Sending to them has stopped.',
    },
    {
      label: 'Deals',
      value: formatInteger(stats.deals) || '—',
      icon: PiHandshake,
    },
  ]

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        {statCards.map((stat) => {
          const card = (
            <div
              key={stat.label}
              className="flex items-center gap-3 bg-surface rounded-lg border border-line p-3.5"
            >
              <div
                className={cn(
                  'size-9 rounded-lg flex items-center justify-center shrink-0',
                  stat.isDanger
                    ? 'bg-error-bg text-error'
                    : 'bg-brand-active/10 text-brand-active'
                )}
              >
                <stat.icon size={16} />
              </div>
              <div className="min-w-0">
                <p className="text-sm text-fg-secondary truncate">
                  {stat.label}
                </p>
                <p
                  className={cn(
                    'text-lg font-bold leading-tight',
                    stat.value === '—'
                      ? 'text-fg-muted'
                      : stat.isDanger
                        ? 'text-error'
                        : 'text-fg'
                  )}
                >
                  {stat.value}
                </p>
                {stat.sublabel && (
                  <p className="mt-0.5 text-xs text-fg-secondary">
                    {stat.sublabel}
                  </p>
                )}
              </div>
            </div>
          )
          if (!stat.tooltip) return card
          return (
            <OverlayTrigger
              key={stat.label}
              placement="top"
              overlay={<Tooltip>{stat.tooltip}</Tooltip>}
            >
              {card}
            </OverlayTrigger>
          )
        })}
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <div className="rounded-lg border border-line bg-surface p-5 shadow-[0px_1px_3px_0px_rgba(16,24,40,0.08),0px_1px_2px_-1px_rgba(16,24,40,0.04)]">
          <div className="mb-5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h3 className="text-base text-fg">Sequence Progress</h3>
              {!isComplete && (
                <span
                  className={cn(
                    'rounded-full px-2 py-0.5 text-xs font-medium tracking-[-0.2px]',
                    isPaused
                      ? 'bg-warning-bg-subtle text-warning-strong'
                      : 'bg-success-bg-subtle text-success-strong'
                  )}
                >
                  {isPaused ? 'Paused' : 'Sending'}
                </span>
              )}
            </div>
            {onViewSequence && (
              <button
                type="button"
                onClick={onViewSequence}
                className="text-sm font-medium text-brand-bright hover:text-brand-active tracking-[-0.2px] transition-colors"
              >
                View Sequence
              </button>
            )}
          </div>
          <SequenceProgressSection
            sequence={sequence}
            stepStats={insights?.summary.sequence_step_stats}
            totalContacts={stats.contacts}
            isComplete={isComplete}
          />
        </div>

        <div className="rounded-lg border border-line bg-surface p-5 shadow-[0px_1px_3px_0px_rgba(16,24,40,0.08),0px_1px_2px_-1px_rgba(16,24,40,0.04)]">
          <div className="mb-5 flex items-center justify-between">
            <h3 className="text-base text-fg">Recent Replies</h3>
            {onTabChange && (
              <button
                type="button"
                onClick={() => onTabChange('replies')}
                className="text-sm font-medium text-brand-bright hover:text-brand-active tracking-[-0.2px] transition-colors"
              >
                View All Replies
              </button>
            )}
          </div>
          {recentReplies.length > 0 ? (
            <div className="divide-y divide-gray-200 space-y-0">
              {recentReplies.map((reply) => {
                const sentiment = SENTIMENT_CONFIG[getSentiment(reply)]
                return (
                  <div
                    key={reply.id}
                    className="group cursor-pointer py-3 first:pt-0 last:pb-0 hover:bg-gray-50 -mx-1 rounded px-1 transition-colors"
                    onClick={() => onTabChange?.('replies')}
                  >
                    <div className="mb-1 flex items-center justify-between">
                      <span className="text-sm text-gray-900 group-hover:text-black transition-colors">
                        {reply.from_name || reply.from_email_address}
                      </span>
                      <span className="text-xs text-gray-600">
                        {formatRelativeTime(reply.date_received)}
                      </span>
                    </div>
                    <p className="mb-1.5 truncate text-sm text-gray-700 group-hover:text-gray-900 transition-colors">
                      {getReplyPreview(reply)}
                    </p>
                    <span
                      className={cn(
                        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs',
                        sentiment.color
                      )}
                    >
                      {sentiment.label}
                    </span>
                  </div>
                )
              })}
            </div>
          ) : (
            <div className="py-8 text-center">
              <PiChat className="mx-auto mb-2 size-8 text-gray-400" />
              <p className="text-sm text-gray-600">No replies yet</p>
            </div>
          )}
        </div>
      </div>
      {isComplete && (
        <Alert
          color="success"
          title="Campaign Completed"
          description="This campaign finished sending all configured sequence steps."
        />
      )}
    </div>
  )
}

/** Per-message metric row shared by an original and each of its variants. */
function VariantMetricRow({
  label,
  subject,
  stat,
  paused = false,
}: {
  label?: string
  subject: string
  stat?: CampaignInsightsSequenceStepStat
  paused?: boolean
}) {
  // A variant with no stats row yet has never been sent — show dashes rather
  // than zeros, which would read as a real "0 opens / 0 replies" result.
  const metric = (value: number | undefined) =>
    stat ? formatInteger(value ?? 0) : '-'
  return (
    <div className="rounded-lg border border-line bg-surface-alt px-3 py-2">
      <div className="mb-1.5 flex items-center gap-2">
        {label && (
          <span className="shrink-0 rounded-full bg-ds2026-badge-primary-bg px-2 py-0.5 text-xxs font-medium text-ds2026-badge-primary-text">
            {label}
          </span>
        )}
        {paused && (
          // A paused variant's low/zero share is intentional, not "losing".
          <span className="shrink-0 rounded-full bg-surface-hover px-2 py-0.5 text-xxs font-medium text-fg-secondary">
            Paused
          </span>
        )}
        <span className="max-w-[260px] truncate text-sm text-gray-900">
          {subject || 'No subject line'}
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-600">
        <span>{metric(stat?.sent)} sent</span>
        <span>{metric(stat?.unique_opens)} opens</span>
        <span>{metric(stat?.unique_replies)} replies</span>
        <span>{metric(stat?.interested)} interested</span>
        <span>{metric(stat?.unsubscribed)} unsub</span>
        <span>{metric(stat?.bounced)} bounced</span>
      </div>
    </div>
  )
}

function SequenceProgressSection({
  sequence,
  stepStats,
  totalContacts,
  isComplete,
}: {
  sequence?: Sequence | null
  stepStats?: CampaignInsightsSequenceStepStat[] | undefined
  totalContacts: number
  isComplete: boolean
}) {
  const { enableSequenceVariants } = useFeatureFlagService()
  const families = useMemo(
    () =>
      groupStepsByFamily(sequence?.steps ?? [], {
        enabled: enableSequenceVariants,
      }),
    [enableSequenceVariants, sequence?.steps]
  )

  if (!sequence?.steps?.length) {
    return (
      <div className="py-8 text-center">
        <PiPaperPlaneTilt className="mx-auto mb-2 size-8 text-fg-muted" />
        <p className="text-sm text-fg-secondary">No sequence steps yet</p>
      </div>
    )
  }

  const statForStep = (stepId: number) =>
    stepStats?.find((stepStat) => stepStat.sequence_step_id === stepId)

  return (
    <div className="space-y-5">
      {families.map((family, index) => {
        const step = family.original
        const stat = statForStep(step.id)
        const totalSent = stat?.sent ?? 0
        const interested = stat?.interested ?? 0
        const replied = stat?.unique_replies ?? 0
        const progress =
          isComplete && totalSent > 0
            ? 100
            : totalContacts > 0
              ? Math.min(100, Math.round((totalSent / totalContacts) * 100))
              : 0

        return (
          <div key={step.id}>
            <div className="mb-2 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <span className="w-3 text-sm text-gray-600">{index + 1}</span>
                <span className="max-w-[220px] truncate text-sm text-gray-900">
                  {step.email_subject || `Step ${index + 1}`}
                </span>
              </div>
              <span className="text-sm text-gray-600">{progress}%</span>
            </div>
            <div className="ml-5">
              <div className="h-2.5 w-full overflow-hidden rounded-full bg-gray-200">
                <div
                  className={cn(
                    'h-full rounded-full transition-all',
                    progress > 0 ? 'bg-brand-bright' : 'bg-gray-200'
                  )}
                  style={{ width: `${progress}%` }}
                />
              </div>
              {family.variants.length === 0 ? (
                <div className="mt-1.5 flex items-center gap-4">
                  <span className="text-xs text-gray-600">
                    {totalSent} sent
                  </span>
                  <span className="text-xs text-gray-600">
                    {interested} interested
                  </span>
                  <span className="text-xs text-gray-600">
                    {replied} replied
                  </span>
                </div>
              ) : (
                // Split-test family: compare the original and each variant
                // side by side, each joined to its own per-step stats.
                <div className="mt-2 space-y-1.5">
                  <VariantMetricRow
                    label={family.originalLabel ?? 'Original'}
                    subject={step.email_subject}
                    stat={stat}
                  />
                  {family.variants.map((variant) => (
                    <VariantMetricRow
                      key={variant.step.id}
                      label={variant.label}
                      subject={variant.step.email_subject}
                      stat={statForStep(variant.step.id)}
                      paused={variant.step.active === false}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default CampaignDashboardOverviewTab
